import axios, { AxiosInstance } from 'axios';
import { useAuthStore } from '../stores/authStore';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';
const APP_VERSION = import.meta.env.VITE_APP_VERSION || 'dev';

/** Ordinary calls; a multipart upload gets UPLOAD_TIMEOUT_MS instead. */
export const DEFAULT_TIMEOUT_MS = 30000;
export const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;

let loggedServerVersion: string | null = null;

class ApiClient {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: DEFAULT_TIMEOUT_MS,
    });

    // Request interceptor to add auth token
    this.instance.interceptors.request.use(
      (config) => {
        config.headers['X-App-Version'] = APP_VERSION;
        // The timeout covers the upload itself, so a multipart body on a slow
        // uplink tripped the 30 s limit while the server still finished (Jim's
        // 8 MB SWPPP: "timed out" five times, stored five times, 2026-10-20).
        if (
          config.data instanceof FormData &&
          config.timeout === DEFAULT_TIMEOUT_MS
        ) {
          config.timeout = UPLOAD_TIMEOUT_MS;
        }
        const token = useAuthStore.getState().accessToken;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          // Debug: Log token status (first/last 10 chars only for security)
          if (
            import.meta.env.DEV ||
            localStorage.getItem('debug_api') === 'true'
          ) {
            const tokenPreview =
              token.length > 20
                ? `${token.substring(0, 10)}...${token.substring(token.length - 10)}`
                : '[short token]';
            console.log(
              `[API] Request to ${config.url} with token: ${tokenPreview}`
            );
          }
        } else {
          // Debug: Warn if no token available
          if (
            import.meta.env.DEV ||
            localStorage.getItem('debug_api') === 'true'
          ) {
            console.warn(`[API] Request to ${config.url} WITHOUT auth token`);
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token refresh and errors
    this.instance.interceptors.response.use(
      (response) => {
        const serverVersion = response.headers?.['x-server-version'];
        if (
          serverVersion &&
          serverVersion !== APP_VERSION &&
          serverVersion !== loggedServerVersion
        ) {
          loggedServerVersion = serverVersion;
          console.warn(
            `[version-skew] client=${APP_VERSION} server=${serverVersion} — reload recommended`
          );
          // The API just told us a newer build exists. Ask the SW to fetch it
          // now instead of waiting for the next timer/foreground check —
          // UpdatePrompt then applies it via the normal safe-screen rules.
          // (Guarded once per skew value by loggedServerVersion above.)
          void import('../utils/pwa').then(({ checkForUpdate }) =>
            checkForUpdate()
          );
        }
        return response;
      },
      async (error) => {
        // Handle timeout errors with a clearer message
        if (
          error.code === 'ECONNABORTED' ||
          error.message?.includes('timeout')
        ) {
          const timeoutError = new Error(
            'Request timed out. The server may be busy or the operation is taking too long. Please try again.'
          );
          (timeoutError as any).isTimeout = true;
          (timeoutError as any).originalError = error;
          return Promise.reject(timeoutError);
        }

        // Handle network errors
        if (!error.response && error.message === 'Network Error') {
          console.error(
            '[API:netfail] Network Error on',
            error.config?.method,
            error.config?.url,
            {
              baseURL: error.config?.baseURL,
              code: error.code,
              hasAuth: !!error.config?.headers?.Authorization,
              requestHeaders: error.config?.headers,
              xhrStatus: error.request?.status,
              xhrReadyState: error.request?.readyState,
              xhrResponseURL: error.request?.responseURL,
              axiosJSON:
                typeof error.toJSON === 'function' ? error.toJSON() : null,
              swController: !!globalThis.navigator?.serviceWorker?.controller,
            }
          );
          const networkError = new Error(
            'Unable to connect to the server. Please check your internet connection.'
          );
          (networkError as any).isNetworkError = true;
          (networkError as any).originalConfig = error.config;
          (networkError as any).originalCode = error.code;
          (networkError as any).xhrStatus = error.request?.status;
          (networkError as any).xhrReadyState = error.request?.readyState;
          return Promise.reject(networkError);
        }

        const originalRequest = error.config;

        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !String(originalRequest.url ?? '').includes('/auth/refresh')
        ) {
          originalRequest._retry = true;

          const refreshToken = useAuthStore.getState().refreshToken;
          if (refreshToken) {
            try {
              // Single-flight: the Lite flow fires ~10 requests on mount, so
              // an expired token produced ~10 parallel refreshes, the later
              // ones failing on the rotated refresh token and logging the
              // inspector out mid-inspection (audit 2026-09-04, H4).
              const accessToken = await this.refreshAccessToken(refreshToken);
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              return this.instance(originalRequest);
            } catch (refreshError: any) {
              // Only a rejected refresh means the session is really gone. A
              // network failure (offline in the field) keeps the session so
              // the inspector's queued work can still sync later.
              //
              // The refresh call runs through this same interceptor, so its
              // own 401 arrives here re-wrapped by the "FastAPI detail"
              // branch below: the status is on `.status`, not on
              // `.response.status`. Reading only the latter meant a dead
              // session was never ended — one stale tab hit prod with 43,792
              // rejected refreshes in a day (2026-09-30).
              const status =
                refreshError?.response?.status ?? refreshError?.status;
              if (status === 401 || status === 403) {
                this.endSession();
              }
              return Promise.reject(refreshError);
            }
          } else if (
            useAuthStore.getState().isAuthenticated &&
            !String(originalRequest.url ?? '').includes('/auth/')
          ) {
            // Signed in on paper, rejected by the server, and nothing to
            // refresh with: the session is gone. Without this the page kept
            // polling with the dead token for as long as it stayed open.
            this.endSession();
          }
        }

        // Extract FastAPI error detail for better error messages
        if (error.response?.data?.detail) {
          const detailError = new Error(error.response.data.detail);
          (detailError as any).status = error.response.status;
          (detailError as any).originalError = error;
          return Promise.reject(detailError);
        }

        return Promise.reject(error);
      }
    );
  }

  /** Drop the stored session and leave for the sign-in page (once). */
  private endSession(): void {
    useAuthStore.getState().logout();
    if (!String(window.location.pathname ?? '').startsWith('/login')) {
      window.location.href = '/login';
    }
  }

  private refreshPromise: Promise<string> | null = null;

  private refreshAccessToken(refreshToken: string): Promise<string> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.instance
        .post('/auth/refresh', { refresh_token: refreshToken })
        .then((response) => {
          const { access_token, refresh_token } = response.data;
          useAuthStore.getState().updateTokens(access_token, refresh_token);
          return access_token as string;
        })
        .finally(() => {
          this.refreshPromise = null;
        });
    }
    return this.refreshPromise;
  }

  get<T>(url: string, config?: any) {
    return this.instance.get<T>(url, config);
  }

  post<T>(url: string, data?: any, config?: any) {
    return this.instance.post<T>(url, data, config);
  }

  put<T>(url: string, data?: any, config?: any) {
    return this.instance.put<T>(url, data, config);
  }

  patch<T>(url: string, data?: any, config?: any) {
    return this.instance.patch<T>(url, data, config);
  }

  delete<T>(url: string, config?: any) {
    return this.instance.delete<T>(url, config);
  }

  request<T>(config: any) {
    return this.instance.request<T>(config);
  }
}

export const api = new ApiClient();
export const apiClient = api; // Alias for backwards compatibility
